import { DOCUMENT, CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Directive, Input, Component, ChangeDetectionStrategy, Inject, HostBinding, NgModule, Pipe } from '@angular/core';
import { fromEvent, forkJoin, Observable } from 'rxjs';
import { filter, tap, map, share, take } from 'rxjs/operators';
import { trigger, transition, style, animate } from '@angular/animations';
import * as i1 from '@angular/router';
import { RouterModule } from '@angular/router';
import { FormControl } from '@angular/forms';

const FADE = trigger('fade', [
    transition('void => *', [style({ opacity: 0 }), animate(300, style({ opacity: 1 }))]),
    transition('* => void', [animate(300, style({ opacity: 0 }))]),
]);

class FocusTrapDirective {
    constructor(el, renderer) {
        this.el = el;
        this.renderer = renderer;
        this.ntsFocusTrap = false;
        this.selectors = ['a[href]', 'button', 'textarea', 'input', 'select'];
        this.$ = this.el.nativeElement;
        this.listeners = [];
    }
    get focus() {
        return Array.from(this.$.querySelectorAll(this.selectors.join(', '))).filter((el) => !el.disabled);
    }
    get first() {
        return this.focus[0];
    }
    get last() {
        return this.focus[this.focus.length - 1];
    }
    ngAfterViewInit() {
        this.run();
    }
    ngOnChanges() {
        this.ngOnDestroy();
        this.run();
    }
    run() {
        if (this.first && this.ntsFocusTrap) {
            this.first.focus();
            this.listeners.push(this.renderer.listen(this.$, 'keydown', (e) => {
                if (e.keyCode !== 9)
                    return;
                if (e.shiftKey) {
                    if (document.activeElement === this.first) {
                        this.last.focus();
                        e.preventDefault();
                    }
                }
                else {
                    if (document.activeElement === this.last) {
                        this.first.focus();
                        e.preventDefault();
                    }
                }
            }));
        }
    }
    ngOnDestroy() {
        this.listeners.forEach((destroy) => destroy());
    }
}
FocusTrapDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.4", ngImport: i0, type: FocusTrapDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive });
FocusTrapDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.2.4", type: FocusTrapDirective, selector: "[ntsFocusTrap]", inputs: { ntsFocusTrap: "ntsFocusTrap", selectors: "selectors" }, usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.4", ngImport: i0, type: FocusTrapDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ntsFocusTrap]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }]; }, propDecorators: { ntsFocusTrap: [{
                type: Input
            }], selectors: [{
                type: Input
            }] } });

class DialogComponent extends FocusTrapDirective {
    constructor(router, document, _el, _renderer) {
        super(_el, _renderer);
        this.router = router;
        this.document = document;
        this._el = _el;
        this._renderer = _renderer;
        this.fade = true;
        this.windowClickClose = false;
        this.validated = false;
        this.destroy = [];
        this.name = 'DIALOG';
        this.windowClicks = fromEvent(this.document, 'click').pipe(filter(({ target }) => (target === null || target === void 0 ? void 0 : target.nodeName) === this.name));
    }
    get scrollbarOffset() {
        return this._el.nativeElement.offsetWidth - this.document.body.clientWidth;
    }
    ngOnInit() {
        this.destroy.push(this.windowClicks.subscribe(this.close.bind(this)));
        this.document.body.style.paddingInlineEnd = `${this.scrollbarOffset}px`;
        this.document.body.style.overflow = 'hidden';
    }
    submit(event) {
        console.log(event);
        event.preventDefault();
        this.validated = true;
    }
    close() {
        this.router.navigate([{ outlets: { dialog: null } }]);
    }
    ngOnDestroy() {
        this.destroy.forEach((sub) => sub.unsubscribe());
        this.document.body.style.paddingInlineEnd = '0px';
        this.document.body.style.overflow = 'initial';
    }
}
DialogComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.4", ngImport: i0, type: DialogComponent, deps: [{ token: i1.Router }, { token: DOCUMENT }, { token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component });
DialogComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.2.4", type: DialogComponent, selector: "dialog[nts]", inputs: { windowClickClose: "windowClickClose" }, host: { properties: { "@fade": "this.fade" } }, usesInheritance: true, ngImport: i0, template: "<main class=\"container my-5\">\r\n  <div class=\"py-5 text-center\">\r\n    <h2 class=\"fw-bold\">Checkout Form Demo</h2>\r\n    <p class=\"lead\">\r\n      Below is an example form built entirely with Bootstrap\u2019s form controls. Each required form group has a validation\r\n      state that can be triggered by attempting to submit the form without completing it.\r\n    </p>\r\n    <a class=\"p-link close\" (click)=\"close()\">&lAarr; Back to docs</a>\r\n  </div>\r\n\r\n  <div class=\"row g-5\">\r\n    <div class=\"col-md-7 col-lg-8 offset-lg-2\">\r\n      <h4 class=\"mb-3\">Billing address</h4>\r\n      <form [class.was-validated]=\"validated\" class=\"needs-validation\" novalidate=\"\" (submit)=\"submit($event)\">\r\n        <div class=\"row g-3\">\r\n          <div class=\"col-sm-6\">\r\n            <label for=\"firstName\" class=\"form-label\">First name</label>\r\n            <input type=\"text\" class=\"form-control\" id=\"firstName\" placeholder=\"\" value=\"\" required=\"\" />\r\n            <div class=\"invalid-feedback\">Valid first name is required.</div>\r\n          </div>\r\n\r\n          <div class=\"col-sm-6\">\r\n            <label for=\"lastName\" class=\"form-label\">Last name</label>\r\n            <input type=\"text\" class=\"form-control\" id=\"lastName\" placeholder=\"\" value=\"\" required=\"\" />\r\n            <div class=\"invalid-feedback\">Valid last name is required.</div>\r\n          </div>\r\n\r\n          <div class=\"col-12\">\r\n            <label for=\"username\" class=\"form-label\">Username</label>\r\n            <div class=\"input-group has-validation\">\r\n              <span class=\"input-group-text\">@</span>\r\n              <input type=\"text\" class=\"form-control\" id=\"username\" placeholder=\"Username\" required=\"\" />\r\n              <div class=\"invalid-feedback\">Your username is required.</div>\r\n            </div>\r\n          </div>\r\n\r\n          <div class=\"col-12\">\r\n            <label for=\"email\" class=\"form-label\">Email <span class=\"text-muted\">(Optional)</span></label>\r\n            <input type=\"email\" class=\"form-control\" id=\"email\" placeholder=\"you@example.com\" />\r\n            <div class=\"invalid-feedback\">Please enter a valid email address for shipping updates.</div>\r\n          </div>\r\n\r\n          <div class=\"col-12\">\r\n            <label for=\"address\" class=\"form-label\">Address</label>\r\n            <input type=\"text\" class=\"form-control\" id=\"address\" placeholder=\"1234 Main St\" required=\"\" />\r\n            <div class=\"invalid-feedback\">Please enter your shipping address.</div>\r\n          </div>\r\n\r\n          <div class=\"col-12\">\r\n            <label for=\"address2\" class=\"form-label\">Address 2 <span class=\"text-muted\">(Optional)</span></label>\r\n            <input type=\"text\" class=\"form-control\" id=\"address2\" placeholder=\"Apartment or suite\" />\r\n          </div>\r\n\r\n          <div class=\"col-md-5\">\r\n            <label for=\"country\" class=\"form-label\">Country</label>\r\n            <select class=\"form-select\" id=\"country\" required=\"\">\r\n              <option value=\"\">Choose...</option>\r\n              <option>United States</option>\r\n            </select>\r\n            <div class=\"invalid-feedback\">Please select a valid country.</div>\r\n          </div>\r\n\r\n          <div class=\"col-md-4\">\r\n            <label for=\"state\" class=\"form-label\">State</label>\r\n            <select class=\"form-select\" id=\"state\" required=\"\">\r\n              <option value=\"\">Choose...</option>\r\n              <option>California</option>\r\n            </select>\r\n            <div class=\"invalid-feedback\">Please provide a valid state.</div>\r\n          </div>\r\n\r\n          <div class=\"col-md-3\">\r\n            <label for=\"zip\" class=\"form-label\">Zip</label>\r\n            <input type=\"text\" class=\"form-control\" id=\"zip\" placeholder=\"\" required=\"\" />\r\n            <div class=\"invalid-feedback\">Zip code required.</div>\r\n          </div>\r\n        </div>\r\n\r\n        <hr class=\"my-4\" />\r\n\r\n        <div class=\"form-check\">\r\n          <input type=\"checkbox\" class=\"form-check-input\" id=\"same-address\" />\r\n          <label class=\"form-check-label\" for=\"same-address\">Shipping address is the same as my billing address</label>\r\n        </div>\r\n\r\n        <div class=\"form-check\">\r\n          <input type=\"checkbox\" class=\"form-check-input\" id=\"save-info\" />\r\n          <label class=\"form-check-label\" for=\"save-info\">Save this information for next time</label>\r\n        </div>\r\n\r\n        <hr class=\"my-4\" />\r\n\r\n        <h4 class=\"mb-3\">Payment</h4>\r\n\r\n        <div class=\"my-3\">\r\n          <div class=\"form-check\">\r\n            <input id=\"credit\" name=\"paymentMethod\" type=\"radio\" class=\"form-check-input\" checked=\"\" required=\"\" />\r\n            <label class=\"form-check-label\" for=\"credit\">Credit card</label>\r\n          </div>\r\n          <div class=\"form-check\">\r\n            <input id=\"debit\" name=\"paymentMethod\" type=\"radio\" class=\"form-check-input\" required=\"\" />\r\n            <label class=\"form-check-label\" for=\"debit\">Debit card</label>\r\n          </div>\r\n          <div class=\"form-check\">\r\n            <input id=\"paypal\" name=\"paymentMethod\" type=\"radio\" class=\"form-check-input\" required=\"\" />\r\n            <label class=\"form-check-label\" for=\"paypal\">PayPal</label>\r\n          </div>\r\n        </div>\r\n\r\n        <div class=\"row gy-3\">\r\n          <div class=\"col-md-6\">\r\n            <label for=\"cc-name\" class=\"form-label\">Name on card</label>\r\n            <input type=\"text\" class=\"form-control\" id=\"cc-name\" placeholder=\"\" required=\"\" />\r\n            <small class=\"text-muted\">Full name as displayed on card</small>\r\n            <div class=\"invalid-feedback\">Name on card is required</div>\r\n          </div>\r\n\r\n          <div class=\"col-md-6\">\r\n            <label for=\"cc-number\" class=\"form-label\">Credit card number</label>\r\n            <input type=\"text\" class=\"form-control\" id=\"cc-number\" placeholder=\"\" required=\"\" />\r\n            <div class=\"invalid-feedback\">Credit card number is required</div>\r\n          </div>\r\n\r\n          <div class=\"col-md-3\">\r\n            <label for=\"cc-expiration\" class=\"form-label\">Expiration</label>\r\n            <input type=\"text\" class=\"form-control\" id=\"cc-expiration\" placeholder=\"\" required=\"\" />\r\n            <div class=\"invalid-feedback\">Expiration date required</div>\r\n          </div>\r\n\r\n          <div class=\"col-md-3\">\r\n            <label for=\"cc-cvv\" class=\"form-label\">CVV</label>\r\n            <input type=\"text\" class=\"form-control\" id=\"cc-cvv\" placeholder=\"\" required=\"\" />\r\n            <div class=\"invalid-feedback\">Security code required</div>\r\n          </div>\r\n        </div>\r\n\r\n        <hr class=\"my-4\" />\r\n\r\n        <button class=\"w-100 btn btn-primary btn-lg\" type=\"submit\">Continue to checkout</button>\r\n      </form>\r\n    </div>\r\n  </div>\r\n</main>\r\n", styles: [":host{align-items:center;background:initial;border:initial;display:flex;height:100%;left:0;padding:0;position:fixed;top:0;width:100%;z-index:9999;-webkit-backdrop-filter:blur(14px) brightness(90%) grayscale(1);backdrop-filter:blur(14px) brightness(90%) grayscale(1);overflow:auto;transition:-webkit-backdrop-filter .3s ease;transition:backdrop-filter .3s ease;transition:backdrop-filter .3s ease,-webkit-backdrop-filter .3s ease;flex-wrap:wrap}:host:after{-webkit-backdrop-filter:inherit;backdrop-filter:inherit;content:\"\";height:100%;left:0;position:fixed;top:0;width:100%;z-index:-1;transition:-webkit-backdrop-filter .3s ease;transition:backdrop-filter .3s ease;transition:backdrop-filter .3s ease,-webkit-backdrop-filter .3s ease}\n"], animations: [FADE], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.4", ngImport: i0, type: DialogComponent, decorators: [{
            type: Component,
            args: [{ selector: 'dialog[nts]', changeDetection: ChangeDetectionStrategy.OnPush, animations: [FADE], template: "<main class=\"container my-5\">\r\n  <div class=\"py-5 text-center\">\r\n    <h2 class=\"fw-bold\">Checkout Form Demo</h2>\r\n    <p class=\"lead\">\r\n      Below is an example form built entirely with Bootstrap\u2019s form controls. Each required form group has a validation\r\n      state that can be triggered by attempting to submit the form without completing it.\r\n    </p>\r\n    <a class=\"p-link close\" (click)=\"close()\">&lAarr; Back to docs</a>\r\n  </div>\r\n\r\n  <div class=\"row g-5\">\r\n    <div class=\"col-md-7 col-lg-8 offset-lg-2\">\r\n      <h4 class=\"mb-3\">Billing address</h4>\r\n      <form [class.was-validated]=\"validated\" class=\"needs-validation\" novalidate=\"\" (submit)=\"submit($event)\">\r\n        <div class=\"row g-3\">\r\n          <div class=\"col-sm-6\">\r\n            <label for=\"firstName\" class=\"form-label\">First name</label>\r\n            <input type=\"text\" class=\"form-control\" id=\"firstName\" placeholder=\"\" value=\"\" required=\"\" />\r\n            <div class=\"invalid-feedback\">Valid first name is required.</div>\r\n          </div>\r\n\r\n          <div class=\"col-sm-6\">\r\n            <label for=\"lastName\" class=\"form-label\">Last name</label>\r\n            <input type=\"text\" class=\"form-control\" id=\"lastName\" placeholder=\"\" value=\"\" required=\"\" />\r\n            <div class=\"invalid-feedback\">Valid last name is required.</div>\r\n          </div>\r\n\r\n          <div class=\"col-12\">\r\n            <label for=\"username\" class=\"form-label\">Username</label>\r\n            <div class=\"input-group has-validation\">\r\n              <span class=\"input-group-text\">@</span>\r\n              <input type=\"text\" class=\"form-control\" id=\"username\" placeholder=\"Username\" required=\"\" />\r\n              <div class=\"invalid-feedback\">Your username is required.</div>\r\n            </div>\r\n          </div>\r\n\r\n          <div class=\"col-12\">\r\n            <label for=\"email\" class=\"form-label\">Email <span class=\"text-muted\">(Optional)</span></label>\r\n            <input type=\"email\" class=\"form-control\" id=\"email\" placeholder=\"you@example.com\" />\r\n            <div class=\"invalid-feedback\">Please enter a valid email address for shipping updates.</div>\r\n          </div>\r\n\r\n          <div class=\"col-12\">\r\n            <label for=\"address\" class=\"form-label\">Address</label>\r\n            <input type=\"text\" class=\"form-control\" id=\"address\" placeholder=\"1234 Main St\" required=\"\" />\r\n            <div class=\"invalid-feedback\">Please enter your shipping address.</div>\r\n          </div>\r\n\r\n          <div class=\"col-12\">\r\n            <label for=\"address2\" class=\"form-label\">Address 2 <span class=\"text-muted\">(Optional)</span></label>\r\n            <input type=\"text\" class=\"form-control\" id=\"address2\" placeholder=\"Apartment or suite\" />\r\n          </div>\r\n\r\n          <div class=\"col-md-5\">\r\n            <label for=\"country\" class=\"form-label\">Country</label>\r\n            <select class=\"form-select\" id=\"country\" required=\"\">\r\n              <option value=\"\">Choose...</option>\r\n              <option>United States</option>\r\n            </select>\r\n            <div class=\"invalid-feedback\">Please select a valid country.</div>\r\n          </div>\r\n\r\n          <div class=\"col-md-4\">\r\n            <label for=\"state\" class=\"form-label\">State</label>\r\n            <select class=\"form-select\" id=\"state\" required=\"\">\r\n              <option value=\"\">Choose...</option>\r\n              <option>California</option>\r\n            </select>\r\n            <div class=\"invalid-feedback\">Please provide a valid state.</div>\r\n          </div>\r\n\r\n          <div class=\"col-md-3\">\r\n            <label for=\"zip\" class=\"form-label\">Zip</label>\r\n            <input type=\"text\" class=\"form-control\" id=\"zip\" placeholder=\"\" required=\"\" />\r\n            <div class=\"invalid-feedback\">Zip code required.</div>\r\n          </div>\r\n        </div>\r\n\r\n        <hr class=\"my-4\" />\r\n\r\n        <div class=\"form-check\">\r\n          <input type=\"checkbox\" class=\"form-check-input\" id=\"same-address\" />\r\n          <label class=\"form-check-label\" for=\"same-address\">Shipping address is the same as my billing address</label>\r\n        </div>\r\n\r\n        <div class=\"form-check\">\r\n          <input type=\"checkbox\" class=\"form-check-input\" id=\"save-info\" />\r\n          <label class=\"form-check-label\" for=\"save-info\">Save this information for next time</label>\r\n        </div>\r\n\r\n        <hr class=\"my-4\" />\r\n\r\n        <h4 class=\"mb-3\">Payment</h4>\r\n\r\n        <div class=\"my-3\">\r\n          <div class=\"form-check\">\r\n            <input id=\"credit\" name=\"paymentMethod\" type=\"radio\" class=\"form-check-input\" checked=\"\" required=\"\" />\r\n            <label class=\"form-check-label\" for=\"credit\">Credit card</label>\r\n          </div>\r\n          <div class=\"form-check\">\r\n            <input id=\"debit\" name=\"paymentMethod\" type=\"radio\" class=\"form-check-input\" required=\"\" />\r\n            <label class=\"form-check-label\" for=\"debit\">Debit card</label>\r\n          </div>\r\n          <div class=\"form-check\">\r\n            <input id=\"paypal\" name=\"paymentMethod\" type=\"radio\" class=\"form-check-input\" required=\"\" />\r\n            <label class=\"form-check-label\" for=\"paypal\">PayPal</label>\r\n          </div>\r\n        </div>\r\n\r\n        <div class=\"row gy-3\">\r\n          <div class=\"col-md-6\">\r\n            <label for=\"cc-name\" class=\"form-label\">Name on card</label>\r\n            <input type=\"text\" class=\"form-control\" id=\"cc-name\" placeholder=\"\" required=\"\" />\r\n            <small class=\"text-muted\">Full name as displayed on card</small>\r\n            <div class=\"invalid-feedback\">Name on card is required</div>\r\n          </div>\r\n\r\n          <div class=\"col-md-6\">\r\n            <label for=\"cc-number\" class=\"form-label\">Credit card number</label>\r\n            <input type=\"text\" class=\"form-control\" id=\"cc-number\" placeholder=\"\" required=\"\" />\r\n            <div class=\"invalid-feedback\">Credit card number is required</div>\r\n          </div>\r\n\r\n          <div class=\"col-md-3\">\r\n            <label for=\"cc-expiration\" class=\"form-label\">Expiration</label>\r\n            <input type=\"text\" class=\"form-control\" id=\"cc-expiration\" placeholder=\"\" required=\"\" />\r\n            <div class=\"invalid-feedback\">Expiration date required</div>\r\n          </div>\r\n\r\n          <div class=\"col-md-3\">\r\n            <label for=\"cc-cvv\" class=\"form-label\">CVV</label>\r\n            <input type=\"text\" class=\"form-control\" id=\"cc-cvv\" placeholder=\"\" required=\"\" />\r\n            <div class=\"invalid-feedback\">Security code required</div>\r\n          </div>\r\n        </div>\r\n\r\n        <hr class=\"my-4\" />\r\n\r\n        <button class=\"w-100 btn btn-primary btn-lg\" type=\"submit\">Continue to checkout</button>\r\n      </form>\r\n    </div>\r\n  </div>\r\n</main>\r\n", styles: [":host{align-items:center;background:initial;border:initial;display:flex;height:100%;left:0;padding:0;position:fixed;top:0;width:100%;z-index:9999;-webkit-backdrop-filter:blur(14px) brightness(90%) grayscale(1);backdrop-filter:blur(14px) brightness(90%) grayscale(1);overflow:auto;transition:-webkit-backdrop-filter .3s ease;transition:backdrop-filter .3s ease;transition:backdrop-filter .3s ease,-webkit-backdrop-filter .3s ease;flex-wrap:wrap}:host:after{-webkit-backdrop-filter:inherit;backdrop-filter:inherit;content:\"\";height:100%;left:0;position:fixed;top:0;width:100%;z-index:-1;transition:-webkit-backdrop-filter .3s ease;transition:backdrop-filter .3s ease;transition:backdrop-filter .3s ease,-webkit-backdrop-filter .3s ease}\n"] }]
        }], ctorParameters: function () {
        return [{ type: i1.Router }, { type: Document, decorators: [{
                        type: Inject,
                        args: [DOCUMENT]
                    }] }, { type: i0.ElementRef }, { type: i0.Renderer2 }];
    }, propDecorators: { fade: [{
                type: HostBinding,
                args: ['@fade']
            }], windowClickClose: [{
                type: Input
            }] } });

class DialogModule {
}
DialogModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.4", ngImport: i0, type: DialogModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DialogModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.2.4", ngImport: i0, type: DialogModule, declarations: [DialogComponent], imports: [CommonModule, RouterModule], exports: [DialogComponent] });
DialogModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.2.4", ngImport: i0, type: DialogModule, imports: [[CommonModule, RouterModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.4", ngImport: i0, type: DialogModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, RouterModule],
                    exports: [DialogComponent],
                    declarations: [DialogComponent],
                }]
        }] });

class FocusTrapModule {
}
FocusTrapModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.4", ngImport: i0, type: FocusTrapModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FocusTrapModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.2.4", ngImport: i0, type: FocusTrapModule, declarations: [FocusTrapDirective], imports: [CommonModule], exports: [FocusTrapDirective] });
FocusTrapModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.2.4", ngImport: i0, type: FocusTrapModule, imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.4", ngImport: i0, type: FocusTrapModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [FocusTrapDirective],
                    exports: [FocusTrapDirective],
                }]
        }] });

/** Is the input a form array  */
const isFormArray = (c) => !!(c === null || c === void 0 ? void 0 : c.controls) && Array.isArray(c.controls);
/** Is the input a form group  */
const isFormGroup = (f) => !!(f === null || f === void 0 ? void 0 : f.controls) && !Array.isArray(f.controls);
/**
 * Manage relationships between a form group mega model and a REST endpoint. When this method is called, it will automatically determine which models in the form group need to be saved and where to save them. Includes POST/PUT/PATCH/DELETE.
 * @author Jerrol Krause
 * @example
 * // Call to save changes within the model
 * saveFormGroup$(this.http, formGroupModel, formGroup).subscribe()
 * // Sample form group model
 * export const formGroupModel: FormGroup2ApiModel[] = [
  {
    path: 'loanApplication',
    uniqueID: 'guid',
    ignoreProps: ['borrowers', 'property'],
    apiUrl: apiBase + '/loanapplications',
  },
  {
    path: 'loanApplication.borrowers',
    uniqueID: 'guid',
    parent: {
      uniqueID: 'guid',
      parentID: 'loanApplicationGUID',
    },
    apiUrl: (parent: any) => apiBase + '/loanapplications/' + parent[0]?.loanApplicationGUID + '/borrowers',
    apiUrls: {
      patch: apiBase + '/borrowers',
    },
    ignoreProps: [
      'borrowers',
      'assets',
      'currentAddress',
      'realEstateOwned',
      'liabilities',
      'gifts',
      'incomes',
      'employments',
      'demographics',
      'declarations',
    ],
  },
  {
    path: 'loanApplication.borrowers.assets',
    uniqueID: 'guid',
    parent: {
      uniqueID: 'guid',
      parentID: 'borrowerGUID',
    },
    apiUrls: {
      patch: apiBase + '/assets',
      delete: apiBase + '/assets',
    },
    apiUrl: (parent: Models.Borrower) => apiBase + '/borrowers/' + parent.guid + '/assets',
  },
 ]
 * @param models
 * @param formGroup
 * @returns
 */
const saveFormGroup$ = (http, models, formGroup) => {
    const changes = models
        // Extract just the requested abstract controls from the form group using the model
        .map((m) => {
        // Split the path to send to the nested model function
        const path = m.path.split('.');
        return getNestedModels(http, path, m, formGroup);
    })
        .reduce((a, b) => [...a, ...b], [])
        .map((m) => {
        // Check if any props have been changed
        const props = getChangedProps(m.control, m.model);
        // If props have been changed and a form control is present, get http request
        if (props && m.control) {
            return modelToHttpRequest(http, m.model, m.control);
        }
        return false;
    })
        // Remove any false items in the array, IE they have no changed props
        .filter((m) => !!m);
    // Remove all dirty flags
    formGroup.markAsPristine();
    return changes.length ? forkJoin(changes) : false;
};
/**
 * Recurse through an abstract control and extract the nested abstract controls as requested by the model. Supports any combination of deeply nested objects and arrays.
 * @param path
 * @param http
 * @param model
 * @param formGroup
 */
const getNestedModels = (http, path, model, formGroup) => {
    // Current path location
    const pathCurrent = path[0];
    // Get next path in series
    const pathNext = [...path].slice(1);
    // Get form control for current path
    const control = formGroup.get(pathCurrent);
    // If current control is a form array
    if (isFormArray(control)) {
        // Loop through array
        return control.controls
            .map((c) => {
            // Need to determine if next path is a form group or another nested array
            const pathGoto = pathNext.length ? pathNext : path;
            return getNestedModels(http, pathGoto, model, c);
        })
            .reduce((a, b) => [...a, ...b], []);
        // If form group
    }
    else if (isFormGroup(control)) {
        return getNestedModels(http, pathNext, model, control);
    }
    else {
        // Destination path, return form group and model
        return [
            {
                control: formGroup,
                model: model,
            },
        ];
    }
};
/**
 * Takes a form group and returns the object with only the properties that have changed. Only returns shallow changes.
 * @param fg
 * @param options
 * @returns If form control has changed properties, return only those changes. False if no changes
 */
const getChangedProps = (fg, model) => {
    var _a, _b, _c, _d, _e, _f;
    if (!(fg === null || fg === void 0 ? void 0 : fg.dirty) || isFormArray(fg) || !isFormGroup(fg)) {
        return false;
    }
    let obj = {};
    // Loop through all the keys in the control
    Object.keys(fg.controls)
        // Remove any keys in the ignored props array
        .filter((key) => { var _a; return !((_a = model === null || model === void 0 ? void 0 : model.ignoreProps) === null || _a === void 0 ? void 0 : _a.includes(key)); })
        .forEach((key) => {
        // Get the nested control
        const control = fg.get(key);
        // If the control is dirty, add this property to the object
        if (control === null || control === void 0 ? void 0 : control.dirty) {
            obj[key] = control.value;
        }
    });
    // If unique ID is specified, get it from the form group and attach
    // Only attach if the object already has at least one changed prop
    if ((model === null || model === void 0 ? void 0 : model.uniqueID) && Object.keys(obj).length && (fg === null || fg === void 0 ? void 0 : fg.value[model.uniqueID])) {
        obj[model.uniqueID] = fg.value[model.uniqueID];
    }
    if (((_a = model === null || model === void 0 ? void 0 : model.parent) === null || _a === void 0 ? void 0 : _a.uniqueID) && ((_b = model === null || model === void 0 ? void 0 : model.parent) === null || _b === void 0 ? void 0 : _b.parentID) && Object.keys(obj).length && ((_d = (_c = fg === null || fg === void 0 ? void 0 : fg.parent) === null || _c === void 0 ? void 0 : _c.parent) === null || _d === void 0 ? void 0 : _d.value)) {
        // Get parent model
        const parent = (_f = (_e = fg === null || fg === void 0 ? void 0 : fg.parent) === null || _e === void 0 ? void 0 : _e.parent) === null || _f === void 0 ? void 0 : _f.value;
        // Get parent uniqueId
        const parentID = parent[model.parent.uniqueID];
        // Attach parent ID to child
        obj[model.parent.parentID] = parentID;
    }
    // If keepNills is NOT set, remove all nil entries
    if (!!obj && Object.keys(obj).length && !(model === null || model === void 0 ? void 0 : model.keepNils)) {
        obj = removeNils$1(obj);
    }
    // If object has at least one changed prop, return obj. Otherwise false
    return Object.keys(obj).length ? obj : false;
};
/**
 * Takes a mega model and a form control and returns an observable of the correct REST api interaction (POST/PUT/PATCH)
 * @param http
 * @param model
 * @param control
 * @param options
 * @returns
 */
const modelToHttpRequest = (http, model, control) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
    // Get default data from the form control
    let data = control.value;
    // Get parent data
    // If parent is form array, get parents parent
    const parentData = isFormArray(control === null || control === void 0 ? void 0 : control.parent) ? (_b = (_a = control === null || control === void 0 ? void 0 : control.parent) === null || _a === void 0 ? void 0 : _a.parent) === null || _b === void 0 ? void 0 : _b.value : (_c = control.parent) === null || _c === void 0 ? void 0 : _c.value;
    // Get unique ID or guid of this model
    const uniqueId = data[model.uniqueID];
    // console.log('modelToHttpRequest', model, control);
    // Get api URL, determine if string or function. Pass parent to function for callback
    let apiUrl = typeof model.apiUrl === 'function' ? model.apiUrl(parentData, control) : model.apiUrl;
    // Hold the http request
    let httpReqest;
    /**
     * Model contains the deleted flag, this request is a DELETE
     */
    if (data[(model === null || model === void 0 ? void 0 : model.deleteProp) || '$$deleted']) {
        // If PUT has an override url, use that instead of the default
        if ((_d = model === null || model === void 0 ? void 0 : model.apiUrls) === null || _d === void 0 ? void 0 : _d.delete) {
            apiUrl =
                typeof model.apiUrls.delete === 'function' ? (_e = model === null || model === void 0 ? void 0 : model.apiUrls) === null || _e === void 0 ? void 0 : _e.delete(parentData, control) : model.apiUrls.delete;
        }
        // By default append the unique ID, skip if disabled
        apiUrl = !!((_f = model.disableAppendID) === null || _f === void 0 ? void 0 : _f.delete) ? apiUrl : apiUrl + '/' + data[model.uniqueID];
        httpReqest = http.delete(apiUrl).pipe(
        // On a successful return response
        tap(() => deleteApiResponse(model, control)));
        /**
         * UniqueID exists and replace is requested, this request is a PUT
         */
    }
    else if (uniqueId && (model === null || model === void 0 ? void 0 : model.replace)) {
        // If PUT has an override url, use that instead of the default
        if ((_g = model === null || model === void 0 ? void 0 : model.apiUrls) === null || _g === void 0 ? void 0 : _g.put) {
            apiUrl = typeof model.apiUrls.put === 'function' ? (_h = model === null || model === void 0 ? void 0 : model.apiUrls) === null || _h === void 0 ? void 0 : _h.put(parentData, control) : model.apiUrls.put;
        }
        // By default append the unique ID, skip if disabled
        apiUrl = !!((_j = model.disableAppendID) === null || _j === void 0 ? void 0 : _j.put) ? apiUrl : apiUrl + '/' + data[model.uniqueID];
        httpReqest = http.put(apiUrl, data);
        /**
         * UniqueID exists, this request is a PATCH
         */
    }
    else if (uniqueId) {
        data = getChangedProps(control, model);
        // If PATCH has an override url, use that instead of the default
        if ((_k = model === null || model === void 0 ? void 0 : model.apiUrls) === null || _k === void 0 ? void 0 : _k.patch) {
            apiUrl =
                typeof model.apiUrls.patch === 'function' ? (_l = model === null || model === void 0 ? void 0 : model.apiUrls) === null || _l === void 0 ? void 0 : _l.patch(parentData, control) : model.apiUrls.patch;
        }
        // By default append the unique ID, skip if disabled
        apiUrl = !!((_m = model.disableAppendID) === null || _m === void 0 ? void 0 : _m.patch) ? apiUrl : apiUrl + '/' + data[model.uniqueID];
        httpReqest = http.patch(apiUrl, data);
        /**
         * No uniqueID exists, this request is a POST
         */
    }
    else {
        data = getChangedProps(control, model);
        // If POST has an override url, use that instead of the default
        if ((_o = model === null || model === void 0 ? void 0 : model.apiUrls) === null || _o === void 0 ? void 0 : _o.post) {
            apiUrl =
                typeof model.apiUrls.post === 'function' ? (_p = model === null || model === void 0 ? void 0 : model.apiUrls) === null || _p === void 0 ? void 0 : _p.post(parentData, control) : model.apiUrls.post;
        }
        // By default append the unique ID, skip if disabled
        httpReqest = http.post(apiUrl, data);
    }
    // Return the completed request
    return httpReqest.pipe(
    // On a successful return response
    tap((apiResponse) => syncApiResponse(model, control, apiResponse)));
};
/**
 * Manage deletions
 * @param model
 * @param control
 * @param parent
 */
const deleteApiResponse = (model, control) => {
    const parent = control.parent;
    const parentData = parent === null || parent === void 0 ? void 0 : parent.value;
    if (!parent) {
        console.warn('Parent does not exist', model, control);
        return;
    }
    // If form array, delete the item out of the entry
    if (isFormArray(parent)) {
        parentData.forEach((v, i) => {
            // Check for a uniqueID match between the current model and the item in the array
            if (v[model.uniqueID] === control.value[model.uniqueID]) {
                parent.removeAt(i);
            }
        });
    }
    else {
        console.warn('Need a condition for a non-array deletion');
    }
};
/**
 * Takes a form group and an api response and adds all the data from the api response to the form group
 * @param model
 * @param control
 * @param apiResponse
 */
const syncApiResponse = (model, control, apiResponse) => {
    var _a;
    // console.warn('syncApiResponse', model, control, apiResponse);
    if (apiResponse && typeof apiResponse === 'string' && model.uniqueID) {
        (_a = control.get(String(model.uniqueID))) === null || _a === void 0 ? void 0 : _a.patchValue(apiResponse);
    }
    else if (apiResponse && typeof apiResponse === 'object' && isFormArray(control.parent)) {
        control.patchValue(apiResponse);
        // @TODO: This is handled by the wizard, not sure if that should be the case
        // control.parent.push(control);
        // If any other type, patch back in response to control. IE like a guid so that subsequent responses are PUT/PATCH instead of POST
    }
    else if (apiResponse && typeof apiResponse === 'object' && !Array.isArray(apiResponse) && isFormGroup(control)) {
        // Loop through all the object properties in the api response
        Object.keys(apiResponse).forEach((key) => {
            // Try to get a form control for each
            const c = control.get(key);
            // If no control or property exists, add it
            // Patchvalue will not add new properties so items like a guid on an api response won't get added
            if (!c) {
                control.addControl(key, new FormControl());
            }
        });
        // After all controls have been created, patch in all data
        control.patchValue(apiResponse);
    }
};
/**
 * Remove nils from a JS object. Includes empty strings arrays and objects
 * @param o
 * @returns
 */
const removeNils$1 = (o) => JSON.parse(JSON.stringify(o), (_key, value) => {
    if (value === null ||
        value === undefined ||
        value === '' ||
        value === [] ||
        value === {} ||
        (typeof value === 'object' && Array.isArray(value) && value.length === 0) ||
        (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0)) {
        return undefined;
    }
    if (typeof value === 'object' && Array.isArray(value) && value.length) {
        const val = value.filter((d) => !!d);
        return val.length ? val : undefined;
    }
    return value;
});

/**
 * Resize and scale an image file in the browser
 * @param image an object of type File
 * @param maxDimension a number for the maximum height and/or width of the new image file
 * @author John Edwards
 * @example
 * // Feed an uploaded file and max dimensions. Function returns a Promise
 * imageResize(file, 850).then((newFile: any) => {
 *   file = newFile;
 * })
 *
 * //async/await
 * const newFile = await resizeImage(file, 850);
 * @returns Promise<File>
 */
const imageResize = (image, maxDimension) => new Promise((resolve, reject) => {
    makeImg(image).then((img) => {
        const newD = downscaleDimension(img, maxDimension);
        const canvas = document.createElement('canvas');
        canvas.width = newD.width;
        canvas.height = newD.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            reject();
            return;
        }
        ctx.drawImage(img, 0, 0, newD.width, newD.height);
        ctx.canvas.toBlob((blob) => {
            // Null check
            if (!blob) {
                reject();
                return;
            }
            resolve(new File([blob], renameToJpg(image.name), {
                type: 'image/jpeg',
            }));
        }, 'image/jpeg');
    });
});
/**
 * Create an HtmlImageElement and set its src to the file object url
 * @param image an object of type File
 * @example
 * //helper function used in resizeImage
 * @returns Promise<File>
 */
const makeImg = (image) => new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.src = URL.createObjectURL(image);
});
/**
 * Check image height and width against limit and resize accordingly
 * @param {Object} dimensions - object containing height and width numbers
 * @param {number} dimensions.height - image height.
 * @param {number} dimensions.width - image width.
 * @param limit - max dimension number
 * @example
 * //helper function used in resizeImage
 * @returns dimensions object
 */
const downscaleDimension = ({ height, width }, limit) => {
    if (height > width && height > limit) {
        return { height: limit, width: width * (limit / height) };
    }
    else if (width > height && width > limit) {
        return { height: height * (limit / width), width: limit };
    }
    else {
        return { height, width };
    }
};
/**
 * Convert file name into .jpg string
 * @param fname - the file name string to be converted
 * @example
 * //helper function used in resizeImage
 * @returns dimensions object
 */
const renameToJpg = (fname) => {
    const pos = fname.lastIndexOf('.');
    return fname.substr(0, pos < 0 ? fname.length : pos) + '.jpg';
};

/**
 * Remove all nill values from a javascript data structure. This tool supports recursion and will remove all nil values from deeply nested objects/arrays.
 *
 * Removes: Nulls, undefined, empty string, empty arrays, objects with no properties
 *
 * Does not remove: 0, false
 * @param o
 * @returns
 */
const removeNils = (o) => JSON.parse(JSON.stringify(o), (_key, value) => {
    if (value === null ||
        value === undefined ||
        value === '' ||
        value === [] ||
        value === {} ||
        (typeof value === 'object' && Array.isArray(value) && value.length === 0) ||
        (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0)) {
        return undefined;
    }
    if (typeof value === 'object' && Array.isArray(value) && value.length) {
        const val = value.filter((d) => !!d);
        return val.length ? val : undefined;
    }
    return value;
});

// A cache that keep track of what scripts have been loaded by their request url
let scriptsLoaded = {};
/**
 * Load a remote javascript file asychronously. Supports caching and multiple concurrent requesters
 * @param srcUrl A url of a javascript file
 * @author Jerrol Krause
 * @example
 * // Single script
 * scriptLoad$('https://unpkg.com/dayjs@1.8.21/dayjs.min.js').subscribe(() => {
 * // Script loaded successfully
 * })
 *
 * // Multiple scripts
 * scriptLoad$(['https://unpkg.com/dayjs@1.8.21/dayjs.min.js', 'https://cdnjs.cloudflare.com/ajax/libs/jquery/3.6.0/jquery.min.js']).subscribe(() => {
 * // Script loaded successfully
 * })
 *
 * // Returns a boolean to indicate success/fail
 * public isLoaded$ = scriptLoad$(['https://unpkg.com/dayjs@1.8.21/dayjs.min.js']); // Returns
 *
 * <div *ngIf="isLoaded$ | async"></div>
 * @returns void
 */
const scriptLoad$ = (srcUrl) => {
    const str = Array.isArray(srcUrl) ? srcUrl : [srcUrl];
    return forkJoin(str.map((s) => scriptLoad(s))).pipe(
    // Flatten any array into a single boolean
    map((results) => results.reduce((a, b) => (a === false ? false : b), true)));
};
/**
 * Load a remote javascript file asychronously. Supports caching and multiple concurrent requesters
 * @param srcUrl A url of a javascript file
 * @author Jerrol Krause
 * @example
 * scriptLoad$('https://unpkg.com/dayjs@1.8.21/dayjs.min.js').subscribe(() => {
 * // Script loaded successfully
 * })
 * @returns void
 */
const scriptLoad = (srcUrl) => new Observable((obs) => {
    // Check of this script has already been successfully loaded
    if (scriptsLoaded[srcUrl]) {
        obs.next(true);
        obs.complete();
        return;
    }
    // Create new script instance
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = srcUrl;
    script.async = true;
    // On successful load
    script.onload = () => {
        // Update record of scripts that have been loaded
        scriptsLoaded = Object.assign(Object.assign({}, scriptsLoaded), { [srcUrl]: true });
        obs.next(true);
        obs.complete();
    };
    // Handle errors
    script.onerror = () => {
        obs.error(false);
        obs.complete();
    };
    // Insert into dom
    document.getElementsByTagName('body')[0].appendChild(script);
    // Share supports edge case of multiple subscribers requesting script at the same time
    // take is not really necessary but still a good pattern to follow to keep the observable cold
}).pipe(share(), take(1));

class TimeAgoPipe {
    /**
     * @param inputDate: Date | Moment
     */
    transform(inputDate) {
        if (!inputDate || (!inputDate.getTime && !inputDate.toDate)) {
            return 'Invalid date';
        }
        const past = inputDate.toDate ? inputDate.toDate() : inputDate.getTime();
        const now = +new Date();
        if (past > now) {
            return 'in the future';
        }
        for (let i = 0, l = TimeAgoPipe.MAPPER.length, ms = now - past, div = TimeAgoPipe.YEAR_MS; i < l; ++i) {
            const elm = TimeAgoPipe.MAPPER[i];
            const unit = Math.floor(ms / (div /= elm.div));
            if (unit >= 1) {
                return unit === 1 ? elm.single : `${unit} ${elm.many} ago`;
            }
        }
        return 'just now';
    }
}
TimeAgoPipe.YEAR_MS = 1000 * 60 * 60 * 24 * 7 * 4 * 12;
TimeAgoPipe.MAPPER = [
    { single: 'last year', many: 'years', div: 1 },
    { single: 'last month', many: 'months', div: 12 },
    { single: 'last week', many: 'weeks', div: 4 },
    { single: 'yesterday', many: 'days', div: 7 },
    { single: 'an hour ago', many: 'hours', div: 24 },
    { single: 'just now', many: 'minutes', div: 60 },
];
TimeAgoPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.4", ngImport: i0, type: TimeAgoPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
TimeAgoPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.2.4", ngImport: i0, type: TimeAgoPipe, name: "timeAgo" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.4", ngImport: i0, type: TimeAgoPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'timeAgo',
                }]
        }] });

class UtilsModule {
}
UtilsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.4", ngImport: i0, type: UtilsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
UtilsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.2.4", ngImport: i0, type: UtilsModule, declarations: [TimeAgoPipe], imports: [CommonModule], exports: [TimeAgoPipe] });
UtilsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.2.4", ngImport: i0, type: UtilsModule, imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.4", ngImport: i0, type: UtilsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [TimeAgoPipe],
                    exports: [TimeAgoPipe],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { DialogComponent, DialogModule, FocusTrapDirective, FocusTrapModule, TimeAgoPipe, UtilsModule, getChangedProps, getNestedModels, imageResize, modelToHttpRequest, removeNils, saveFormGroup$, scriptLoad$ };
